import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/floating-artworks.css';
import { allArtworksQuery } from '../lib/queries';
import { useSanityPreview } from '../lib/useSanityPreview';
import { client } from '../lib/sanityClient';

interface Artwork {
  _id: string;
  title: string;
  artist: string;
  slug: string;
  year: number;
  medium: string;
  imageUrl: string;
  artistImageUrl?: string;
  price: number;
  edition: {
    size: number;
    available: number;
  };
}

interface FloatingArtwork {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  imageUrl: string;
  artwork: Artwork;
  isClicked?: boolean;
}

const FloatingArtworks: React.FC = () => {
  // Add more debug information
  console.log('FloatingArtworks component mounted');
  console.log('Sanity query:', allArtworksQuery);
  
  const { data: artworks, error, isLoading } = useSanityPreview<Artwork[]>(allArtworksQuery);
  
  const [floatingArtworks, setFloatingArtworks] = useState<FloatingArtwork[]>([]);
  const [fadeOverlay, setFadeOverlay] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Create mock artworks for testing if real data isn't available
  const createMockArtworks = () => {
    console.log('Creating mock artworks for testing');
    return Array.from({ length: 10 }, (_, i) => ({
      _id: `mock-${i}`,
      title: `Mock Artwork ${i+1}`,
      artist: 'Test Artist',
      slug: `mock-${i}`,
      year: 2023,
      medium: 'Digital',
      imageUrl: `https://picsum.photos/seed/${i}/300/300`,
      price: 1000,
      edition: { size: 10, available: 5 }
    }));
  };
  
  // Decide what artworks to use - real ones or mock ones
  const displayArtworks = artworks && artworks.length > 0 ? artworks : createMockArtworks();
  
  // Additional logging to debug data loading
  useEffect(() => {
    console.log('Artworks data loaded:', artworks);
    console.log('Loading state:', isLoading);
    if (error) console.error('Error loading artworks:', error);
    
    // Attempt direct Sanity client fetch as a backup
    if (!artworks && !isLoading) {
      console.log('Attempting direct Sanity client fetch');
      client.fetch(allArtworksQuery)
        .then(data => console.log('Direct fetch result:', data))
        .catch(err => console.error('Direct fetch error:', err));
    }
  }, [artworks, isLoading, error]);
  const animationFrameRef = useRef<number | null>(null);

  // Initialize floating artworks
  useEffect(() => {
    if (displayArtworks && displayArtworks.length > 0 && containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      // New artwork size
      const ARTWORK_SIZE = 75; // px
      // Create floating artworks with random positions and velocities (slower)
      const initialArtworks = displayArtworks.map((artwork) => {
        // Random position
        const x = Math.random() * (containerWidth - ARTWORK_SIZE);
        const y = Math.random() * (containerHeight - ARTWORK_SIZE);

        // Random velocity (halve the previous speed)
        const vx = ((Math.random() * 2 + 1) * 0.5) * (Math.random() > 0.5 ? 1 : -1);
        const vy = ((Math.random() * 2 + 1) * 0.5) * (Math.random() > 0.5 ? 1 : -1);

        return {
          id: artwork._id,
          x,
          y,
          vx,
          vy,
          imageUrl: artwork.imageUrl,
          artwork,
        };
      });
      
      setFloatingArtworks(initialArtworks);
    }
  }, [displayArtworks]);

  // Animation loop for floating artworks
  useEffect(() => {
    if (floatingArtworks.length === 0 || !containerRef.current) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    // Use the actual artwork size used for rendering
    const artworkSize = 75; // Diameter in pixels (must match CSS and initialization)
    
    const animate = () => {
      setFloatingArtworks((prevArtworks) => {
        const nextArtworks = prevArtworks.map((artwork) => {
          // Calculate new position
          let newX = artwork.x + artwork.vx;
          let newY = artwork.y + artwork.vy;
          let newVx = artwork.vx;
          let newVy = artwork.vy;

          // Bounce off walls
          if (newX <= 0 || newX >= containerWidth - artworkSize) {
            newVx = -newVx;
            newX = newX <= 0 ? 0 : containerWidth - artworkSize;
          }
          if (newY <= 0 || newY >= containerHeight - artworkSize) {
            newVy = -newVy;
            newY = newY <= 0 ? 0 : containerHeight - artworkSize;
          }

          return {
            ...artwork,
            x: newX,
            y: newY,
            vx: newVx,
            vy: newVy,
          };
        });

        // Collision detection & response (elastic for equal mass)
        for (let i = 0; i < nextArtworks.length; i++) {
          for (let j = i + 1; j < nextArtworks.length; j++) {
            const a = nextArtworks[i];
            const b = nextArtworks[j];
            const dx = b.x - a.x;
            const dy = b.y - a.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < artworkSize) {
              // Overlap: simple elastic collision for equal mass
              // Calculate normal
              const nx = dx / dist;
              const ny = dy / dist;
              // Relative velocity
              const dvx = b.vx - a.vx;
              const dvy = b.vy - a.vy;
              // Projected velocity along normal
              const relVel = dvx * nx + dvy * ny;
              if (relVel < 0) {
                // Exchange velocities along normal direction
                const impulse = relVel;
                nextArtworks[i].vx += impulse * nx;
                nextArtworks[i].vy += impulse * ny;
                nextArtworks[j].vx -= impulse * nx;
                nextArtworks[j].vy -= impulse * ny;
                // Separate circles so they don't stick
                const overlap = artworkSize - dist;
                const sepX = nx * (overlap / 2);
                const sepY = ny * (overlap / 2);
                nextArtworks[i].x -= sepX;
                nextArtworks[i].y -= sepY;
                nextArtworks[j].x += sepX;
                nextArtworks[j].y += sepY;
              }
            }
          }
        }

        return nextArtworks;
      });
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    // Cleanup animation on unmount or when conditions change
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [floatingArtworks]);

  // Handle artwork click with a simple fade transition
  const handleArtworkClick = (artwork: FloatingArtwork) => {
    // Stop the animation loop
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Find the clicked artwork
    const clickedArtwork = floatingArtworks.find(a => a.id === artwork.id);
    if (!clickedArtwork) return;
    
    // Mark the clicked artwork so we can keep it visible while others fade
    const updatedArtworks = floatingArtworks.map(art => ({
      ...art,
      isClicked: art.id === clickedArtwork.id
    }));
    setFloatingArtworks(updatedArtworks);
    
    // Fade the overlay
    setFadeOverlay(true);
    
    // Navigate after a short delay
    setTimeout(() => {
      navigate(`/works/${artwork.artwork.slug}`);
      // Reset states
      setFadeOverlay(false);
    }, 400);
  };

  // Handle "Enter" button click to skip animation
  const handleEnterClick = () => {
    if (displayArtworks && displayArtworks.length > 0) {
      navigate(`/works/${displayArtworks[0].slug}`);
    }
  };

  // Loading state with timeout to prevent infinite loading
  if (isLoading) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center">
        <div className="mb-4">Loading artworks...</div>
        <div className="text-sm text-gray-500">If loading takes too long, using test data</div>
      </div>
    );
  }

  // Error state
  if (error) {
    console.error('Error loading artworks:', error);
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center">
        <div className="text-red-500 mb-4">Error loading artworks: {error.message}</div>
        <div className="text-sm">Using test data instead</div>
      </div>
    );
  }

  // Show floating artworks only (never render Works here)
  return (
    <div className="floating-artworks-container" ref={containerRef}>
      {floatingArtworks.length > 0 ? (
        floatingArtworks.map((artwork) => (
        <div
          key={artwork.id}
          className={`floating-artwork ${fadeOverlay && !artwork.isClicked ? 'fade-out' : ''} ${artwork.isClicked ? 'clicked' : ''}`}
          style={{
            left: `${artwork.x}px`,
            top: `${artwork.y}px`,
          }}
          onClick={() => handleArtworkClick(artwork)}
        >
          <img src={artwork.imageUrl} alt={artwork.artwork.title} />
        </div>
      ))
      ) : (
        // Show a message if no floating artworks are available yet
        <div className="w-full h-screen flex items-center justify-center">
          <div className="text-xl">Loading artwork visualization...</div>
        </div>
      )}
      
      {/* Removed expanding artwork animation for simplicity */}
      
      {/* Fade overlay */}
      <div className={`fade-overlay ${fadeOverlay ? 'active' : ''}`}></div>
      
      {/* Enter button to skip animation */}
      <button className="enter-button" onClick={handleEnterClick}>
        Enter Gallery
      </button>
    </div>
  );
};

export default FloatingArtworks;
